import { useEffect } from 'react';

/**
 * Generic hook to toggle properties on target elements based on validation of source elements.
 *
 * Accepts either a single config object, or an array of configs to apply multiple.
 *
 * @param {Object|Object[]} configOrArray - Configuration object or array of configuration objects.
 *
 * Each configuration object:
 * @param {string} config.sourceId - ID of the source element (input, select, etc).
 * @param {string} config.targetId - ID of the target element to update.
 * @param {(el: HTMLElement) => boolean} config.validate - Validation function.
 * @param {string} config.property - Property name to set on the target element.
 * @param {*} config.valueIfInvalid - Value to set if validation fails.
 * @param {string} [config.eventName="input"] - Event to listen on source element.
 *
 */
export const useToggleProperty = (configOrArray) => {
  useEffect(() => {
    const configs = Array.isArray(configOrArray)
      ? configOrArray
      : [configOrArray];
    const cleanups = [];

    configs.forEach((config) => {
      const {
        sourceId,
        targetId,
        validate,
        property,
        valueIfInvalid,
        eventName = 'input',
      } = config;

      const sourceEl = document.getElementById(sourceId);
      const targetEl = document.getElementById(targetId);

      if (!sourceEl || !targetEl) return;

      const handler = () => {
        const isValid = validate(sourceEl);
        targetEl[property] = isValid ? !valueIfInvalid : valueIfInvalid;
      };

      sourceEl.addEventListener(eventName, handler);
      handler();

      cleanups.push(() => sourceEl.removeEventListener(eventName, handler));
    });

    return () => cleanups.forEach((clean) => clean());
  }, [configOrArray]);
};

export default useToggleProperty;
